import React from 'react'
import { css, cx } from '@emotion/css'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { VrApiExportHover } from '../../export/vr-api-export-hover'

const marginSize = '20px'
export const responsiveLayoutWidth = '1200px'

const cssBorder = css`
  border: solid 1px ${theme.color.neutral50};
  border-radius: 4px;
`

const cssList = css`
  margin: ${marginSize};
  * {
    -webkit-column-break-inside: avoid; /* Chrome, Safari, Opera */
    page-break-inside: avoid; /* Firefox */
    break-inside: avoid; /* IE 10+ */
  }
  @media all and (min-width: ${responsiveLayoutWidth}) {
    columns: 2;
    column-gap: ${marginSize};
  }
  @media all and (max-width: ${responsiveLayoutWidth}) {
    display: flex;
    flex-direction: column;
  }
  > *:not(:last-child) {
    margin-bottom: ${marginSize};
  }
`

const cssRowBase = css`
  position: relative; /* This is needed for the on hover cloud icon positioning */
  column-span: all;
`

const cssRowStyled = cx(
  cssRowBase,
  cssBorder,
  css`
    background: ${theme.color.white};
    width: 100%;
  `
)

const cssRowChildren = css`
  display: flex;
  flex-wrap: nowrap;
  width: 100%;
  > * {
    flex: 1;
  }
  @media all and (max-width: ${responsiveLayoutWidth}) {
    flex-direction: column;
  }
  @media all and (min-width: ${responsiveLayoutWidth}) {
    flex-direction: row;
  }
`
const cssColumn = cx(
  cssBorder,
  css`
    background: ${theme.color.white};
    display: flex;
    flex-wrap: nowrap;
    flex-direction: column;
    flex-grow: 1;
    > * {
      flex-grow: 1;
    }
    > *:not(:last-child) {
      margin-bottom: ${marginSize};
    }
  `
)

const cssModule = css`
  background: ${theme.color.white};
  height: 100%;
  overflow: hidden; /* This is needed to avoid page breaks for firefox */
  position: relative; /* This is needed for the on hover cloud icon positioning */

  flex-grow: 1;
  display: flex;
  justify-content: center;
  border-radius: 4px;
`

const cssModuleContent = css`
  display: flex;
  flex-direction: column;
  padding: 28px;
  width: 100%;
`

const cssStandaloneModuleContent = cx(
  cssModuleContent,
  css`
    padding: 0;
  `
)

const cssBorderedModule = cx(cssBorder, cssModule)

interface VrModuleProps {
  readonly className?: string
  readonly order: number
  readonly hasBorder?: boolean
  readonly minHeight: number
  readonly flex?: string
  readonly onExportClick?: () => void
  readonly children: React.ReactNode
  readonly testId?: string
  readonly exportButtonE2eTestId?: string
}

interface VrGroupProps {
  readonly order: number
  readonly onExportClick?: () => void
  readonly children: React.ReactNode
  readonly exportButtonE2eTestId?: string
}

export function VrModuleList(props: { readonly children: React.ReactNode }) {
  return <div className={cssList} children={props.children} />
}

export function VrModuleRow(props: VrGroupProps) {
  const { order, onExportClick, children, exportButtonE2eTestId } = props
  const cssClass = cx(
    css`
      order: ${order};
    `,
    cssRowStyled
  )

  return (
    <VrHoverContent
      onExportClick={onExportClick}
      className={cssClass}
      exportButtonE2eTestId={exportButtonE2eTestId}
    >
      <div className={cssRowChildren}>{children}</div>
    </VrHoverContent>
  )
}

export function VrModuleColumn(props: VrGroupProps) {
  const cssClass = cx(
    css`
      order: ${props.order};
    `,
    cssColumn
  )
  return <div className={cssClass} children={props.children} />
}

export function VrModulePanel(props: VrModuleProps) {
  const {
    onExportClick,
    hasBorder,
    children,
    order,
    minHeight,
    className,
    testId,
    exportButtonE2eTestId
  } = props
  const cssParentClass = cx(
    css`
      order: ${order};
      min-height: ${minHeight}px;
      order: ${order};
    `,
    hasBorder ? cssBorderedModule : cssModule,
    className
  )
  return (
    <div className={cssParentClass} data-e2e={testId} data-testid={testId}>
      <div className={cssModuleContent}>
        <VrHoverContent
          onExportClick={onExportClick}
          exportButtonE2eTestId={exportButtonE2eTestId}
        >
          {children}
        </VrHoverContent>
      </div>
    </div>
  )
}

export function VrStandaloneModulePanel(props: VrModuleProps) {
  const {
    onExportClick,
    hasBorder,
    children,
    order,
    minHeight,
    flex,
    className,
    testId,
    exportButtonE2eTestId
  } = props
  const cssParentClass = cx(
    css`
      order: ${order};
      min-height: ${minHeight}px;
      order: ${order};
    `,
    hasBorder ? cssBorderedModule : cssModule,
    className,
    hasBorder
      ? css`
          border-radius: 4px;
        `
      : undefined
  )
  return (
    <div className={cssParentClass} data-e2e={testId} data-testid={testId}>
      <div className={cssStandaloneModuleContent}>
        <VrHoverContent
          onExportClick={onExportClick}
          exportButtonE2eTestId={exportButtonE2eTestId}
          className={css`
            padding: 28px;
            flex: ${flex || 'none'};
          `}
        >
          {children}
        </VrHoverContent>
      </div>
    </div>
  )
}

function VrHoverContent(props: {
  readonly onExportClick?: () => void
  readonly children: React.ReactNode
  readonly className?: string
  readonly exportButtonE2eTestId?: string
}) {
  const { onExportClick, children, exportButtonE2eTestId } = props
  // eslint-disable-next-line unicorn/prefer-ternary
  if (onExportClick) {
    return (
      <VrApiExportHover
        onExportClick={onExportClick}
        className={props.className}
        exportButtonE2eTestId={exportButtonE2eTestId}
      >
        {children}
      </VrApiExportHover>
    )
  } else {
    return <div className={props.className}>{children}</div>
  }
}
